import { Panel } from '@/components/admin/Field'
import DownloadMp3Button from '@/components/host/DownloadMp3Button'
import { describeError, logError } from '@/lib/errors'
import { lumi } from '@/lib/lumi'
import { titleFromFileName, uploadAudio } from '@/lib/uploadAudio'
import { MAX_HOST_TRACKS, type HostTrack } from '@/types/host'
import { Music2, Pause, Play, Trash2, UploadCloud } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'

function formatSize(bytes?: number) {
  if (!bytes) return ''
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function HostPlaylist({ hostId }: { hostId: string }) {
  const [tracks, setTracks] = useState<HostTrack[]>([])
  const [loading, setLoading] = useState(true)
  const [progress, setProgress] = useState('')
  const [playingId, setPlayingId] = useState('')
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.host_tracks.list({
        filter: { hostId },
        sort: { order: 1 },
        limit: MAX_HOST_TRACKS + 20,
      })
      setTracks((list || []) as HostTrack[])
    } catch (error) {
      console.error('Falha ao carregar playlist:', error)
      toast.error('Não foi possível carregar a playlist.')
    } finally {
      setLoading(false)
    }
  }, [hostId])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    return () => {
      audioRef.current?.pause()
    }
  }, [])

  const onFiles = async (fileList: FileList | null) => {
    const files = Array.from(fileList || [])
    if (!files.length) return
    const room = MAX_HOST_TRACKS - tracks.length
    if (room <= 0) {
      toast.error(`A playlist já tem ${MAX_HOST_TRACKS} músicas (limite máximo).`)
      return
    }
    const accepted = files.slice(0, room)
    if (files.length > room) {
      toast(`Só cabem mais ${room} músicas. As restantes foram ignoradas.`)
    }

    let done = 0
    let order = tracks.reduce((max, item) => Math.max(max, item.order || 0), 0)
    for (const file of accepted) {
      setProgress(`A preparar e enviar ${done + 1} de ${accepted.length}: ${file.name}`)
      try {
        const uploaded = await uploadAudio(file)
        order += 1
        await lumi.entities.host_tracks.create({
          hostId,
          title: titleFromFileName(file.name),
          artist: '',
          fileUrl: uploaded.url,
          fileName: uploaded.fileName,
          sizeBytes: uploaded.sizeBytes,
          order,
          createdAt: new Date().toISOString(),
        })
        done += 1
      } catch (error) {
        logError('enviar música', error)
        toast.error(describeError(error))
      }
    }
    setProgress('')
    if (done) toast.success(`${done} música(s) adicionada(s).`)
    await load()
  }

  const updateField = async (track: HostTrack, field: 'title' | 'artist', value: string) => {
    if (!track._id || (track[field] || '') === value) return
    try {
      await lumi.entities.host_tracks.update(track._id, { [field]: value })
      setTracks((prev) => prev.map((item) => (item._id === track._id ? { ...item, [field]: value } : item)))
    } catch (error) {
      console.error('Falha ao editar música:', error)
      toast.error('Não foi possível guardar a alteração.')
    }
  }

  const remove = async (track: HostTrack) => {
    if (!track._id) return
    if (!window.confirm(`Apagar "${track.title}" da playlist?`)) return
    try {
      if (playingId === track._id) {
        audioRef.current?.pause()
        setPlayingId('')
      }
      await lumi.entities.host_tracks.delete(track._id)
      setTracks((prev) => prev.filter((item) => item._id !== track._id))
      toast.success('Música apagada.')
    } catch (error) {
      logError('apagar música', error)
      toast.error(describeError(error))
    }
  }

  const togglePlay = (track: HostTrack) => {
    if (!track._id) return
    if (playingId === track._id) {
      audioRef.current?.pause()
      setPlayingId('')
      return
    }
    audioRef.current?.pause()
    const audio = new Audio(track.fileUrl)
    audio.onended = () => setPlayingId('')
    audio.onerror = () => {
      setPlayingId('')
      toast.error('Não foi possível tocar esta música.')
    }
    audioRef.current = audio
    setPlayingId(track._id)
    audio.play().catch(() => {
      setPlayingId('')
      toast.error('Não foi possível tocar esta música.')
    })
  }

  const full = tracks.length >= MAX_HOST_TRACKS

  return (
    <Panel title="Minha playlist">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm font-semibold text-slate-700">
          <Music2 size={16} className="mr-1 inline text-[#0b2ea8]" />
          {tracks.length} de {MAX_HOST_TRACKS} músicas
        </p>
        <label
          className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold ${
            full || progress ? 'cursor-not-allowed bg-slate-200 text-slate-500' : 'cursor-pointer bg-[#0b2ea8] text-white'
          }`}
        >
          <UploadCloud size={16} /> Adicionar músicas
          <input
            type="file"
            accept="audio/*,.mp3,.mpeg,.mpg,.mpga,.wav,.ogg,.oga,.m4a,.aac,.flac,.opus,.aiff,.wma"
            multiple
            className="sr-only"
            disabled={full || Boolean(progress)}
            onChange={(e) => {
              void onFiles(e.target.files)
              e.target.value = ''
            }}
          />
        </label>
      </div>
      <div className="mb-4 h-2 overflow-hidden rounded-full bg-slate-100" aria-hidden="true">
        <div className="h-full bg-[#ffd21f]" style={{ width: `${Math.min(100, (tracks.length / MAX_HOST_TRACKS) * 100)}%` }} />
      </div>
      {progress ? <p className="mb-3 rounded-lg bg-blue-50 px-3 py-2 text-xs font-semibold text-[#0b2ea8]">{progress}</p> : null}
      <p className="mb-3 text-xs text-slate-500">Formatos: MP3, MPEG, WAV, OGG, M4A, AAC ou FLAC. Arquivos acima de 9 MB são comprimidos automaticamente. Tudo é convertido para MP3 ao enviar, e o botão azul baixa sempre em MP3.</p>

      {loading ? <div className="h-20 animate-pulse rounded-xl bg-slate-100" /> : null}
      <ul className="space-y-2">
        {tracks.map((track, index) => (
          <li key={track._id} className="flex items-center gap-2 rounded-xl border border-slate-200 p-2 sm:gap-3 sm:p-3">
            <span className="w-6 shrink-0 text-center text-xs font-bold text-slate-400">{index + 1}</span>
            <button
              type="button"
              onClick={() => togglePlay(track)}
              className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#ffd21f] text-[#0b2ea8]"
              aria-label={playingId === track._id ? 'Pausar' : 'Ouvir'}
            >
              {playingId === track._id ? <Pause size={16} fill="currentColor" /> : <Play size={16} fill="currentColor" />}
            </button>
            <div className="grid min-w-0 flex-1 gap-1 sm:grid-cols-2">
              <input
                defaultValue={track.title}
                onBlur={(e) => void updateField(track, 'title', e.target.value.trim() || track.title)}
                aria-label="Título da música"
                className="w-full min-w-0 rounded-lg border border-slate-200 px-2 py-1.5 text-sm font-semibold"
              />
              <input
                defaultValue={track.artist || ''}
                placeholder="Artista"
                onBlur={(e) => void updateField(track, 'artist', e.target.value.trim())}
                aria-label="Artista"
                className="w-full min-w-0 rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
              />
            </div>
            <span className="hidden shrink-0 text-xs text-slate-400 sm:inline">{formatSize(track.sizeBytes)}</span>
            <DownloadMp3Button track={track} />
            <button
              type="button"
              onClick={() => void remove(track)}
              className="inline-flex shrink-0 items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2.5 py-2 text-xs font-bold text-red-600 active:scale-95"
              aria-label={`Apagar ${track.title}`}
            >
              <Trash2 size={15} />
              <span className="hidden sm:inline">Apagar</span>
            </button>
          </li>
        ))}
        {!loading && !tracks.length ? <p className="text-sm text-slate-500">Sua playlist está vazia. Adicione as primeiras músicas.</p> : null}
      </ul>
    </Panel>
  )
}

export default HostPlaylist
